import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { settingsService, surfaceRatesService, costsService, retentionService, staffService } from '../services/api';
import { MEASURES, SOILING, CONDITION, COATS, TRADES, tradeOf, usesDim, usesDim2, dimLabel, dimLabel2, DIM_DEFAULT } from '../utils/measure';
import { COST_UNITS, COST_KINDS } from '../utils/costs';
import './Settings.css';

let rowSeq = 1;
const newRate = (patch = {}) => ({ _key: `n${rowSeq++}`, name: '', measure: 'area', trade: 'wash', rate: '', default_height_m: '', default_dim2_m: '', companion_surface_id: null, colour: '#2DD4BF', is_active: true, ...patch });
const rowFromApi = (r) => ({ ...r, _key: `r${r.id}`, rate: r.rate ?? '', default_height_m: r.default_height_m ?? '', default_dim2_m: r.default_dim2_m ?? '', companion_surface_id: r.companion_surface_id ?? null });
// Cost categories (internal job costing)
let catSeq = 1;
const newCat = () => ({ _key: `c${catSeq++}`, name: '', kind: 'other', unit: 'ea', is_active: true });
const catFromApi = (c) => ({ ...c, _key: `k${c.id}` });

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [rates, setRates] = useState([]);
  const [costCats, setCostCats] = useState([]);
  const [staff, setStaff] = useState([]); // for "Who usually pays?" (backlog #688)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  // Privacy & retention (GID-Session-017, backlog #611)
  const [stale, setStale] = useState(null); // { cutoff, customers } | null
  const [staleChecked, setStaleChecked] = useState({}); // id -> bool
  const [staleBusy, setStaleBusy] = useState(false);
  const [staleMsg, setStaleMsg] = useState('');

  async function load() {
    setLoading(true);
    const [res, sr, cc, st] = await Promise.all([settingsService.get(), surfaceRatesService.getAll(), costsService.categories(), staffService.getAll()]);
    if (res.success) setSettings(res.settings || {});
    if (sr.success) setRates((sr.rates || []).map(rowFromApi));
    if (cc.success) setCostCats((cc.categories || []).map(catFromApi));
    if (st.success) setStaff((st.staff || []).filter((p) => p.is_active));
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function set(key, value) { setSettings((prev) => ({ ...prev, [key]: value })); }
  const bool = (key) => settings[key] === 'true' || settings[key] === true;

  const setRate = (key, patch) => setRates((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  async function removeRate(r) {
    if (r.id) {
      if (!window.confirm(`Remove "${r.name}" from the rate card? Existing quotes keep their figures.`)) return;
      const res = await surfaceRatesService.delete(r.id);
      if (!res.success) { setError(res.error || 'Could not remove that surface'); return; }
    }
    setRates((prev) => prev.filter((x) => x._key !== r._key));
  }
  const setCat = (key, patch) => setCostCats((prev) => prev.map((c) => (c._key === key ? { ...c, ...patch } : c)));
  async function removeCat(c) {
    if (c.id) {
      if (!window.confirm(`Remove "${c.name}"? Existing cost lines keep their figures and show as uncategorised.`)) return;
      const res = await costsService.deleteCategory(c.id);
      if (!res.success) { setError(res.error || 'Could not remove that category'); return; }
    }
    setCostCats((prev) => prev.filter((x) => x._key !== c._key));
  }
  function moveCat(key, dir) {
    setCostCats((prev) => {
      const i = prev.findIndex((c) => c._key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }
  function move(key, dir) {
    setRates((prev) => {
      const i = prev.findIndex((r) => r._key === key);
      if (i < 0) return prev;
      // Rows move within their own trade group (Washing / Painting).
      let j = i + dir;
      while (j >= 0 && j < prev.length && tradeOf(prev[j]) !== tradeOf(prev[i])) j += dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function save() {
    setSaving(true); setMsg(''); setError('');
    const blank = rates.find((r) => !String(r.name || '').trim());
    if (blank) { setError('Every surface on the rate card needs a name.'); setSaving(false); return; }
    if (costCats.some((c) => !String(c.name || '').trim())) { setError('Every cost category needs a name.'); setSaving(false); return; }
    const res = await settingsService.update(settings);
    const sr = await surfaceRatesService.saveAll(rates.map((r, i) => ({
      id: r.id, name: r.name, measure: r.measure, trade: tradeOf(r), rate: Number(r.rate) || 0,
      default_height_m: usesDim(r.measure) ? (r.default_height_m === '' ? null : Number(r.default_height_m)) : null,
      default_dim2_m: usesDim2(r.measure) ? (r.default_dim2_m === '' ? null : Number(r.default_dim2_m)) : null,
      companion_surface_id: r.companion_surface_id || null,
      colour: r.colour, is_active: !!r.is_active, sort_order: i
    })));
    const cc = await costsService.saveCategories(costCats.map((c, i) => ({ id: c.id, name: c.name, kind: c.kind, unit: c.unit, is_active: !!c.is_active, sort_order: i })));
    setSaving(false);
    if (!res.success || !sr.success || !cc.success) { setError(res.error || sr.error || cc.error || 'Failed to save'); return; }
    setSettings(res.settings || settings);
    setRates((sr.rates || []).map(rowFromApi));
    setCostCats((cc.categories || []).map(catFromApi));
    setMsg('Saved');
    setTimeout(() => setMsg(''), 2500);
  }

  // Privacy & retention (backlog #611): find clients whose working relationship has gone quiet.
  async function findStale() {
    setStaleBusy(true); setStaleMsg(''); setError('');
    const years = Number(settings.retention_years) || 3;
    const res = await retentionService.stale({ years });
    setStaleBusy(false);
    if (!res.success) { setError(res.error || 'Could not find stale clients'); return; }
    setStale(res);
    setStaleChecked(Object.fromEntries(res.customers.map((c) => [c.id, true])));
  }
  async function anonymiseSelected() {
    const ids = Object.entries(staleChecked).filter(([, v]) => v).map(([id]) => Number(id));
    if (!ids.length) return;
    if (!window.confirm('This cannot be undone.')) return;
    setStaleBusy(true); setError('');
    const res = await retentionService.anonymise(ids);
    setStaleBusy(false);
    if (!res.success) { setError(res.error || 'Could not anonymise those clients'); return; }
    setStaleMsg(`Anonymised ${res.anonymised}`);
    findStale();
  }

  if (loading) return <div className="empty-state"><div className="loading-spinner"></div></div>;

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <div className="flex items-center gap-sm">
          {msg && <span className="text-success">{msg}</span>}
          {error && <span className="text-error">{error}</span>}
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>

      <section className="card settings-section">
        <h2 className="card-title mb-md">Business details</h2>
        <div className="form-group">
          <label htmlFor="business_name">Business name</label>
          <input id="business_name" value={settings.business_name || ''} onChange={(e) => set('business_name', e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="business_email">Reply-to / from email</label>
          <input id="business_email" type="email" value={settings.business_email || ''} onChange={(e) => set('business_email', e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="timezone">Timezone</label>
          <input id="timezone" value={settings.timezone || ''} readOnly />
        </div>
      </section>

      <section className="card settings-section">
        <h2 className="card-title mb-md">Booking rules (defaults)</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="slot_granularity_minutes">Slot interval (min)</label>
            <input id="slot_granularity_minutes" type="number" min="5" step="5" value={settings.slot_granularity_minutes || ''} onChange={(e) => set('slot_granularity_minutes', e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="default_min_notice_minutes">Min notice (min)</label>
            <input id="default_min_notice_minutes" type="number" min="0" step="30" value={settings.default_min_notice_minutes || ''} onChange={(e) => set('default_min_notice_minutes', e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="default_max_advance_days">Book up to (days ahead)</label>
            <input id="default_max_advance_days" type="number" min="1" value={settings.default_max_advance_days || ''} onChange={(e) => set('default_max_advance_days', e.target.value)} />
          </div>
        </div>
        <p className="text-muted settings-hint">These are defaults — each job type can override its own notice, buffers and horizon.</p>
      </section>

      <section className="card settings-section">
        <h2 className="card-title mb-md">Notifications &amp; public booking</h2>
        <div className="form-group">
          <label htmlFor="reminder_lead_minutes">Reminder lead time (min before)</label>
          <input id="reminder_lead_minutes" type="number" min="0" step="30" value={settings.reminder_lead_minutes || ''} onChange={(e) => set('reminder_lead_minutes', e.target.value)} />
        </div>
        <label className="checkbox-label settings-toggle">
          <input type="checkbox" checked={bool('public_booking_enabled')} onChange={(e) => set('public_booking_enabled', String(e.target.checked))} />
          Public booking page is live
        </label>
        <label className="checkbox-label settings-toggle">
          <input type="checkbox" checked={bool('auto_confirm_bookings')} onChange={(e) => set('auto_confirm_bookings', String(e.target.checked))} />
          Auto-confirm client bookings (otherwise they land as “pending”)
        </label>
      </section>

      <section className="card settings-section">
        <h2 className="card-title mb-md">Privacy &amp; retention</h2>
        <p className="text-muted settings-intro">
          Client details are kept while there is a working relationship. After this many years with no bookings, quotes or jobs,
          they can be anonymised — their jobs and figures stay, their name, contact details, address, notes and map pin go.
        </p>
        <div className="form-group">
          <label htmlFor="retention_years">Keep client details for (years)</label>
          <input id="retention_years" type="number" min="1" max="20" value={settings.retention_years ?? ''} onChange={(e) => set('retention_years', e.target.value)} />
        </div>
        <div className="flex items-center gap-sm">
          <button type="button" className="btn btn-sm btn-outline" onClick={findStale} disabled={staleBusy}>{staleBusy ? 'Looking…' : 'Find clients to anonymise'}</button>
          {staleMsg && <span className="text-success">{staleMsg}</span>}
        </div>
        {stale && (
          stale.customers.length === 0 ? (
            <p className="text-muted mt-md">Nothing older than {stale.cutoff} — all good.</p>
          ) : (
            <div className="retention-list mt-md">
              {stale.customers.map((c) => (
                <label key={c.id} className="retention-row">
                  <input type="checkbox" checked={!!staleChecked[c.id]} onChange={(e) => setStaleChecked((prev) => ({ ...prev, [c.id]: e.target.checked }))} />
                  <span className="retention-name">{c.name}</span>
                  <span className="text-muted">{c.address_suburb || '—'}</span>
                  <span className="text-muted">Last activity {c.last_activity}</span>
                  <span className="text-muted">{c.appointment_count} appt · {c.quote_count} quote{c.quote_count === 1 ? '' : 's'} · {c.job_count} job{c.job_count === 1 ? '' : 's'}</span>
                </label>
              ))}
              <button type="button" className="btn btn-sm btn-danger mt-sm" onClick={anonymiseSelected} disabled={staleBusy || !Object.values(staleChecked).some(Boolean)}>
                Anonymise {Object.values(staleChecked).filter(Boolean).length} client{Object.values(staleChecked).filter(Boolean).length === 1 ? '' : 's'}
              </button>
            </div>
          )
        )}
        <p className="text-muted settings-hint mt-md">No marketing emails to booking clients without their separate opt-in (Spam Act 2003).</p>
      </section>

      <section className="card settings-section">
        <h2 className="card-title mb-md">Reviews</h2>
        <p className="text-muted settings-intro">Reviews are shown on the website, not here — this sends the ask for one after a job is done.</p>
        <label className="checkbox-label settings-toggle">
          <input type="checkbox" checked={bool('review_request_enabled')} onChange={(e) => set('review_request_enabled', String(e.target.checked))} />
          Ask for a Google review when a job is marked complete
        </label>
        <div className="form-group mt-md">
          <label htmlFor="review_url">Google review link</label>
          <input id="review_url" value={settings.review_url || ''} onChange={(e) => set('review_url', e.target.value)} placeholder="https://g.page/r/…/review" />
        </div>
        <div className="form-group">
          <label htmlFor="review_request_delay_hours">Send it after (hours)</label>
          <input id="review_request_delay_hours" type="number" min="0" value={settings.review_request_delay_hours ?? ''} onChange={(e) => set('review_request_delay_hours', e.target.value)} />
        </div>
        <p className="text-muted settings-hint">Where the link comes from: Google Business Profile → Ask for reviews → copy the short link.</p>
      </section>

      <section className="card settings-section">
        <h2 className="card-title mb-md">Quotes</h2>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="abn">ABN</label>
            <input id="abn" value={settings.abn || ''} onChange={(e) => set('abn', e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="business_phone">Phone</label>
            <input id="business_phone" value={settings.business_phone || ''} onChange={(e) => set('business_phone', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="business_address">Business address</label>
          <input id="business_address" value={settings.business_address || ''} onChange={(e) => set('business_address', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="quote_valid_days">Quotes valid for (days)</label>
            <input id="quote_valid_days" type="number" min="1" value={settings.quote_valid_days || ''} onChange={(e) => set('quote_valid_days', e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="quote_counter">Next quote number</label>
            <input id="quote_counter" value={`Q-${(parseInt(settings.quote_counter, 10) || 1000) + 1}`} readOnly />
          </div>
        </div>
        <label className="checkbox-label settings-toggle">
          <input type="checkbox" checked={bool('gst_registered')} onChange={(e) => set('gst_registered', String(e.target.checked))} />
          GST registered (new quotes use GST-inclusive pricing)
        </label>
        <div className="form-group mt-md">
          <label htmlFor="quote_terms">Default quote terms</label>
          <textarea id="quote_terms" rows="4" value={settings.quote_terms || ''} onChange={(e) => set('quote_terms', e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="payment_details">Payment details (printed on quotes)</label>
          <textarea id="payment_details" rows="3" value={settings.payment_details || ''} onChange={(e) => set('payment_details', e.target.value)} placeholder="Bank: … BSB: … Account: …" />
        </div>
        <p className="text-muted settings-hint">These appear on the quote PDF and the client's quote page.</p>
      </section>

      <section className="card settings-section">
        <h2 className="card-title mb-md">Rate card &amp; map estimator</h2>
        <p className="text-muted settings-intro">
          Surfaces you measure on the satellite map. Areas are priced per m², runs per lineal metre, walls or fences by length × height,
          eaves by outer outline minus inner outline (with a width as the fallback estimate) or by length × width.
          Rates are private — they never show on the public booking page.
        </p>

        {TRADES.map((t) => {
          const group = rates.filter((r) => tradeOf(r) === t.value);
          return (
            <div key={t.value} className="rate-trade" data-trade={t.value}>
              <h3 className="settings-subtitle">{t.value === 'paint' ? 'Painting' : 'Pressure washing'}</h3>
              {t.value === 'paint' && <p className="text-muted settings-intro">Painting rates are per coat — each area on the map picks its coats and condition.</p>}
              {group.length === 0 ? (
                <p className="text-muted">No surfaces yet.</p>
              ) : (
                <div className="rate-card">
                  <div className="rate-row rate-row-header">
                    <span /><span>Surface</span><span>Measured as</span><span>Rate{t.value === 'paint' ? ' per coat' : ''}</span><span>Height / width</span><span>Also adds</span><span>On</span><span />
                  </div>
                  {group.map((r, i) => {
                    const info = MEASURES.find((m) => m.value === r.measure) || MEASURES[0];
                    return (
                      <div key={r._key} className={`rate-row ${r.is_active ? '' : 'inactive'}`}>
                        <div className="rate-order">
                          <button type="button" aria-label="Move up" onClick={() => move(r._key, -1)} disabled={i === 0}>▲</button>
                          <button type="button" aria-label="Move down" onClick={() => move(r._key, 1)} disabled={i === group.length - 1}>▼</button>
                        </div>
                        <div className="rate-name">
                          <input type="color" aria-label="Colour on the map" value={r.colour || '#2DD4BF'} onChange={(e) => setRate(r._key, { colour: e.target.value })} />
                          <input aria-label="Surface name" value={r.name} onChange={(e) => setRate(r._key, { name: e.target.value })} placeholder="e.g. Exposed aggregate" />
                        </div>
                        <select aria-label="Measured as" value={r.measure} title={info.hint}
                          onChange={(e) => setRate(r._key, {
                            measure: e.target.value,
                            default_height_m: usesDim(e.target.value) ? (r.default_height_m || DIM_DEFAULT[e.target.value]) : '',
                            default_dim2_m: usesDim2(e.target.value) ? (r.default_dim2_m || 0.1) : ''
                          })}>
                          {MEASURES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                        <div className="rate-money">
                          <span>$</span>
                          <input aria-label="Rate" type="number" min="0" step="0.5" value={r.rate} onChange={(e) => setRate(r._key, { rate: e.target.value })} placeholder="0.00" />
                          <span className="text-muted">/{info.unit === 'lm' ? 'lm' : 'm²'}</span>
                        </div>
                        {usesDim(r.measure)
                          ? (
                            <div className="rate-dims">
                              <div className="rate-money" title={`Default ${dimLabel(r.measure).toLowerCase()} for new shapes`}>
                                {usesDim2(r.measure) && <span className="text-muted rate-dim-label">{dimLabel(r.measure)}</span>}
                                <input aria-label={`Default ${dimLabel(r.measure).toLowerCase()} in metres`} type="number" min="0" step="0.05" value={r.default_height_m} onChange={(e) => setRate(r._key, { default_height_m: e.target.value })} /><span className="text-muted">m</span>
                              </div>
                              {usesDim2(r.measure) && (
                                <div className="rate-money" title={`Default ${dimLabel2(r.measure).toLowerCase()} for new shapes`}>
                                  <span className="text-muted rate-dim-label">{dimLabel2(r.measure)}</span>
                                  <input aria-label={`Default ${dimLabel2(r.measure).toLowerCase()} in metres`} type="number" min="0" step="0.05" value={r.default_dim2_m} onChange={(e) => setRate(r._key, { default_dim2_m: e.target.value })} /><span className="text-muted">m</span>
                                </div>
                              )}
                            </div>
                          )
                          : <span className="text-muted rate-na">—</span>}
                        <select aria-label="Also adds" className="rate-companion" value={r.companion_surface_id || ''} title="Another surface created from the same outline — the map's surface picker offers both together, e.g. Eaves + Gutters"
                          onChange={(e) => setRate(r._key, { companion_surface_id: e.target.value ? Number(e.target.value) : null })}>
                          <option value="">—</option>
                          {group.filter((o) => o.id && o.id !== r.id).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                        </select>
                        <input type="checkbox" aria-label="Active" checked={!!r.is_active} onChange={(e) => setRate(r._key, { is_active: e.target.checked })} />
                        <button type="button" className="rate-remove" aria-label="Remove surface" onClick={() => removeRate(r)}>×</button>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex gap-sm mt-md">
                <button type="button" className="btn btn-sm btn-outline" onClick={() => setRates((prev) => [...prev, newRate({ trade: t.value })])}>+ Add {t.value === 'paint' ? 'painting' : 'washing'} surface</button>
              </div>
            </div>
          );
        })}
        <p className="text-muted settings-hint mt-md">Turn a surface off to hide it from the estimator without losing it. Removing one leaves existing quotes untouched. "Also adds" pairs a surface with another drawn from the same outline — Eaves + Gutters from one roofline.</p>

        <h3 className="settings-subtitle">Soiling factor</h3>
        <p className="text-muted settings-intro">Multiplies the surface rate. Set it per area on the map — heavy soiling on exposed aggregate is a very different job to a light rinse.</p>
        <div className="form-row">
          {SOILING.map((s) => (
            <div className="form-group" key={s.value}>
              <label htmlFor={s.key}>{s.label} ×</label>
              <input id={s.key} type="number" min="0.1" step="0.05" value={settings[s.key] ?? s.fallback} onChange={(e) => set(s.key, e.target.value)} />
            </div>
          ))}
        </div>

        <h3 className="settings-subtitle">Painting — condition and coats</h3>
        <p className="text-muted settings-intro">A painting area is the rate per coat × coats × its condition. Condition covers the prep: sound paintwork is Good; flaking, chalky or bare timber needs more scraping, sanding and priming.</p>
        <div className="form-row">
          {CONDITION.map((s) => (
            <div className="form-group" key={s.value}>
              <label htmlFor={s.key}>{s.label} ×</label>
              <input id={s.key} type="number" min="0.1" step="0.05" value={settings[s.key] ?? s.fallback} onChange={(e) => set(s.key, e.target.value)} />
            </div>
          ))}
          <div className="form-group">
            <label htmlFor="paint_default_coats">Coats to start with</label>
            <select id="paint_default_coats" value={settings.paint_default_coats ?? '2'} onChange={(e) => set('paint_default_coats', e.target.value)}>
              {COATS.map((n) => <option key={n} value={String(n)}>{n}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="min_charge">Minimum charge ($)</label>
            <input id="min_charge" type="number" min="0" step="10" value={settings.min_charge ?? ''} onChange={(e) => set('min_charge', e.target.value)} placeholder="0" />
          </div>
        </div>
        <p className="text-muted settings-hint">If the measured areas come in under this, a top-up line brings the quote up to the minimum. Leave at 0 for none.</p>
        <label className="checkbox-label settings-toggle">
          <input type="checkbox" checked={bool('quote_show_unit_rates')} onChange={(e) => set('quote_show_unit_rates', String(e.target.checked))} />
          Show the per-metre rate on measured lines (PDF and client page). Off = the client sees the area and the amount only.
        </label>
      </section>

      <section className="card settings-section">
        <h2 className="card-title mb-md">Job costs (internal)</h2>
        <p className="text-muted settings-intro">
          What each job actually costs — logged on the appointment under Costs, with receipt photos, against what was quoted.
          Never shown to clients. Each person's hourly cost is set on the Team page.
          Overheads — expenses, the equipment register and the rate every job carries — are on the <Link to="/business-costs">Business costs</Link> page.
        </p>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="home_address">Home base (where every trip starts)</label>
            <input id="home_address" value={settings.home_address || ''} onChange={(e) => set('home_address', e.target.value)} placeholder="Street, suburb, state, postcode" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fuel_l_per_100km">Fuel use (litres per 100 km)</label>
            <input id="fuel_l_per_100km" type="number" min="0" step="0.1" value={settings.fuel_l_per_100km ?? ''} onChange={(e) => set('fuel_l_per_100km', e.target.value)} placeholder="17" />
          </div>
          <div className="form-group">
            <label htmlFor="fuel_price_per_l">Fuel price ($ per litre)</label>
            <input id="fuel_price_per_l" type="number" min="0" step="0.01" value={settings.fuel_price_per_l ?? ''} onChange={(e) => set('fuel_price_per_l', e.target.value)} placeholder="e.g. 2.05" />
          </div>
          <div className="form-group">
            <label htmlFor="cost_km_rate">Travel rate ($ per km)</label>
            <input id="cost_km_rate" type="number" min="0" step="0.01" value={settings.cost_km_rate ?? ''} onChange={(e) => set('cost_km_rate', e.target.value)} placeholder="0.88" />
          </div>
        </div>
        <p className="text-muted settings-hint">"+ Travel" on a job's costs works out the road distance from the home base to the client, one return trip per booked day, and prices the fuel: km × litres per 100 km × price. The per-km rate (ATO 88c for 2025–26) pre-fills plain Travel lines instead.</p>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="default_payer">Who usually pays?</label>
            <select id="default_payer" value={settings.default_payer || ''} onChange={(e) => set('default_payer', e.target.value)}>
              <option value="">Whoever is logged in</option>
              {staff.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <p className="text-muted settings-hint">Every "Paid by" — a receipt, an expense, a job's cash cost line, a piece of equipment — starts as this person; change it on the line when someone else paid. Feeds the Between us ledger on Business costs.</p>

        <h3 className="settings-subtitle">Cost categories</h3>
        {costCats.length === 0 ? (
          <p className="text-muted">No categories yet.</p>
        ) : (
          <div className="cost-cats">
            <div className="cost-cat-row cost-cat-header">
              <span /><span>Category</span><span>Type</span><span>Unit</span><span>On</span><span />
            </div>
            {costCats.map((c, i) => (
              <div key={c._key} className={`cost-cat-row ${c.is_active ? '' : 'inactive'}`}>
                <div className="rate-order">
                  <button type="button" aria-label="Move up" onClick={() => moveCat(c._key, -1)} disabled={i === 0}>▲</button>
                  <button type="button" aria-label="Move down" onClick={() => moveCat(c._key, 1)} disabled={i === costCats.length - 1}>▼</button>
                </div>
                <input aria-label="Category name" value={c.name} onChange={(e) => setCat(c._key, { name: e.target.value })} placeholder="e.g. Fuel" />
                <select aria-label="Type" value={c.kind} title={COST_KINDS.find((k) => k.value === c.kind)?.hint || ''} onChange={(e) => setCat(c._key, { kind: e.target.value })}>
                  {COST_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
                <select aria-label="Default unit" value={c.unit} onChange={(e) => setCat(c._key, { unit: e.target.value })}>
                  {COST_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
                <input type="checkbox" aria-label="Active" checked={!!c.is_active} onChange={(e) => setCat(c._key, { is_active: e.target.checked })} />
                <button type="button" className="rate-remove" aria-label="Remove category" onClick={() => removeCat(c)}>×</button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-sm mt-md">
          <button type="button" className="btn btn-sm btn-outline" onClick={() => setCostCats((prev) => [...prev, newCat()])}>+ Add category</button>
        </div>
        <p className="text-muted settings-hint mt-md">
          The type drives what a new cost line pre-fills: Wages take the job's hours and the person's hourly cost, Travel takes the per-km rate.
          Turning a category off hides it from the picker; removing one leaves existing lines as they are.
        </p>
      </section>
    </div>
  );
}
